import { ReactNode, useEffect, useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import * as Sentry from '@sentry/react';
import { useHistory, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@material-ui/core';
import theme from 'theme/theme';
import { useAppDispatch, useAppSelector } from 'store';
import { setCredentials } from 'store/reducers/auth';
import { useGetCurrentUserQuery, useRefreshTokenQuery, api, refreshUserForecastsInGlobalCache, updateUserInSearchCache } from 'utils/wemble-api';
import store from 'store';

import { MaybeDrafted } from '@reduxjs/toolkit/dist/query/core/buildThunks';
import { UserDto } from 'utils/wemble-api.generated';

import { usePrevious } from 'hooks';
import { getStatusSource } from 'utils/getStatusSource';
import { getTeamsContext } from 'utils/teamsContext';
import io from "socket.io-client";
import moment from 'moment';


const App = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const initialized = useAppSelector((state) => state.auth.initialized);

  const authenticated = useAppSelector((state) => state.auth.authenticated);
  const { data: currentUser, refetch: refetchUser } = useGetCurrentUserQuery();
  const [refreshReady, setRefreshReady] = useState(false);
  useRefreshTokenQuery(refreshReady ? { source: getStatusSource(), viewportWidth: window.innerWidth, viewportHeight: window.innerHeight } : skipToken);
  const prevAuthenticated = usePrevious(authenticated);

  // Generate session ID once and store globally
  const sessionId = useMemo(() => {
    const id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    (window as any)._sessionId = id;
    return id;
  }, []);

  const connectSocket = () => {
    var socket = io({ query: { token: localStorage.getItem('token') } });

    socket.on('error', function (error) {
      console.log(error);
    });

    socket.on("connect_error", (error) => {
      console.log(error);
    });

    socket.on("UPDATE_USER", function (user) {

      let skip = false;

      const updateUserDraft = (draft: MaybeDrafted<UserDto | null | undefined>) => {
        if (!draft || skip) return;
        draft.currentWorkload = user.currentWorkload;
        draft.futureWorkload = user.futureWorkload;
        draft.message = user.message;
        draft.lastUpdated = user.lastUpdated;
        draft.workplaceForecast = user.workplaceForecast;
        draft.atHome = user.atHome;
        draft.onLeave = user.onLeave;
      };


      dispatch(
        // @ts-ignore
        api.util.updateQueryData('getCurrentUser', undefined, (draft) => {

          if (draft?._id == user._id) {
            skip = (window as any)._workloadLastUpdated_ && moment().diff(moment((window as any)._workloadLastUpdated_), 's') < 5
            updateUserDraft(draft);
          }
        }),
      );

      // Update the userSearch cache
      updateUserInSearchCache(dispatch, store.getState, updateUserDraft, user._id);

    });

    socket.on("UPDATE_FORECASTS_FOR_USER", async function (data) {
      // Refresh user forecasts in global cache when other users make changes
      // Skip if the update is from the current session to avoid overwriting their typing
      if ((window as any)._sessionId !== data.sessionId) {
        await refreshUserForecastsInGlobalCache(data.userId, dispatch);
      }
    });
  }

  useEffect(() => {
    let lastActiveTime = Date.now();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        lastActiveTime = Date.now();
      } else if (document.visibilityState === 'visible') {
        onReturnToForeground();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const onReturnToForeground = () => {
      const backgroundDuration = Date.now() - lastActiveTime;

      // Full reload if backgrounded for more than 60 minutes
      if (backgroundDuration > 60 * 60 * 1000) {
        window.location.reload();
        return;
      }

      // @ts-ignore
      dispatch(api.util.updateQueryData('getCurrentUser', undefined, (draft) => {
        if (draft) {
          // @ts-ignore
          dispatch(api.util.invalidateTags([{ type: 'User', id: "ME" }, { type: 'User', id: draft._id }]))
          // @ts-ignore
          setTimeout(() => dispatch(api.util.invalidateTags([{ type: "Competence" }, { type: "Project" }, { type: "Matter" }, { type: "Forecast" }, { type: "User" }, { type: "Group" }, { type: "Office" }])), 300)
        }
      }))
    };

    window['_IosAppEnterForeground'] = onReturnToForeground;
    window['_onReturnToForeground'] = onReturnToForeground;

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!prevAuthenticated && authenticated) {
      refetchUser();
      connectSocket()

    }
  }, [authenticated, prevAuthenticated, refetchUser]);

  useEffect(() => {
    if (!initialized || !authenticated) return;
    // Wait for Teams SDK context (for accurate heartbeat source), capped at 5 seconds.
    // Teams/Outlook: SDK resolves in ~1s → fires quickly with correct source.
    // Web: SDK times out, 5s cap fires → proceeds with "web_app" (correct).
    Promise.race([getTeamsContext(), new Promise<null>(resolve => setTimeout(() => resolve(null), 5000))])
      .then(() => setRefreshReady(true));
  }, [initialized, authenticated]);

  useEffect(() => {
    try {
      (window as any).webkit?.messageHandlers?.setLocation?.postMessage(window.location.pathname);
    } catch { }
  }, [pathname]);

  // Scrolls to top if navigating forward.
  useEffect(() => {
    const unlisten = history.listen(() => {
      if (history.action === 'PUSH') {
        window.scrollTo(0, 0);
      }
    });
    return () => {
      unlisten();
    };
  }, [history]);

  useEffect(() => {
    if (!initialized) {
      dispatch(setCredentials(localStorage.getItem('token') ?? false));
    }
  }, [initialized]);

  useEffect(() => {
    if (currentUser?._id) {
      Sentry.setUser({ id: currentUser._id, email: currentUser.email });
      Sentry.setTag('app.source', getStatusSource());
      Sentry.setTag('app.company', currentUser.email?.split('@')[1]);
    } else if (!authenticated) {
      Sentry.setUser(null);
    }
  }, [currentUser?._id, authenticated]);


  if (!initialized) return null;

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default App;
